import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Tooltip,
  Snackbar,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { PayByLinkDetailModal } from '@/types/modal';
import { usePayByLinkDetail, useDeletePayByLink, useItem } from '@/hooks/item';
import { LoadingBodyModal } from '@/components/molecules/common/Modal';
import { transformDateFrance } from '@/utilities/datetime';

const PayByLinkDetail: React.FC<{ modal: PayByLinkDetailModal }> = ({ modal }) => {
  const { t } = useTranslation();
  const { isViewDetail = true } = modal;
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const itemR = useItem(modal?.item?.productId, false);

  const data = usePayByLinkDetail(modal.item.productId);
  const submitR = useDeletePayByLink(modal.item.productId);

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = (event: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  const handleDelete = () => {
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = () => {
    submitR.mutate();
  };

  useEffect(() => {
    if (submitR.kind === 'Completed') {
      itemR.refetch();
      setShowDeleteConfirm(false);
      modal.onClose();
    }
  }, [submitR.kind]);

  if (data.kind === 'Called') {
    return (
      <Dialog
        open={true}
        onClose={modal.onClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogContent>
          <LoadingBodyModal />
        </DialogContent>
      </Dialog>
    );
  }

  if (data.kind === 'Error') {
    return (
      <Dialog
        open={true}
        onClose={modal.onClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogContent>
          <Typography color="error">{t('modal.pay_by_link_detail.error')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={modal.onClose}>{t('common.close')}</Button>
        </DialogActions>
      </Dialog>
    );
  }

  const payByLinkData = data.data;

  return (
    <>
      <Dialog
        open={true}
        onClose={modal.onClose}
        maxWidth="sm"
        fullWidth
        data-cy="modal-pay-by-link-detail"
      >
        <DialogTitle>{t('modal.pay_by_link_detail.title')}</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="body2"
              paragraph
              whiteSpace="pre-line"
              data-cy="modal-pay-by-link-detail-description"
            >
              {isViewDetail
                ? t('modal.pay_by_link_detail.description', { itemId: modal.item.productId })
                : t('modal.pay_by_link_detail.description_resubmit', { itemId: modal.item.productId })}
            </Typography>

            {payByLinkData.paymentLink && (
              <Box sx={{ mt: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                  >
                    {t('modal.pay_by_link_detail.link')}:
                  </Typography>
                  <Tooltip title={t('common.copy')}>
                    <IconButton
                      onClick={() => handleCopy(payByLinkData.paymentLink || '')}
                      size="small"
                      data-cy="modal-pay-by-link-detail-copy"
                    >
                      <ContentCopyIcon style={{ fontSize: '14px' }} />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Typography
                  component="div"
                  style={{ wordBreak: 'break-all' }}
                  data-cy="modal-pay-by-link-detail-link"
                >
                  {payByLinkData.paymentLink}
                </Typography>
              </Box>
            )}

            {payByLinkData.expiredAt && (
              <Box sx={{ mt: 2 }}>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                >
                  {t('modal.pay_by_link_detail.expiration_date')}:
                </Typography>
                <Typography data-cy="modal-pay-by-link-detail-expiration-date">
                  {transformDateFrance(payByLinkData.expiredAt)?.format('DD/MM/YYYY HH:mm:ss')}
                </Typography>
              </Box>
            )}

            {payByLinkData.status && (
              <Box sx={{ mt: 2 }}>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                >
                  {t('modal.pay_by_link_detail.status')}:
                </Typography>
                <Typography data-cy="modal-pay-by-link-detail-status">{payByLinkData.status}</Typography>
              </Box>
            )}

            {payByLinkData.reason && (
              <Box sx={{ mt: 2 }}>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                >
                  {t('modal.pay_by_link_detail.category')}:
                </Typography>
                <Typography data-cy="modal-pay-by-link-detail-category">{payByLinkData.reason}</Typography>
              </Box>
            )}

            {payByLinkData.formattedAmount && (
              <Box sx={{ mt: 2 }}>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                >
                  {t('modal.pay_by_link_detail.amount')}:
                </Typography>
                <Typography data-cy="modal-pay-by-link-detail-amount">{payByLinkData.formattedAmount}</Typography>
              </Box>
            )}

            {isViewDetail && (
              <>
                <Typography
                  variant="body2"
                  sx={{ mt: 3 }}
                >
                  {t('modal.pay_by_link_detail.delete_description')}
                </Typography>
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          {isViewDetail && (
            <Button
              color="error"
              onClick={handleDelete}
              disabled={submitR.kind === 'Loading'}
              data-cy="modal-pay-by-link-detail-delete"
            >
              {t('modal.pay_by_link_detail.delete_button')}
            </Button>
          )}
          <Button
            onClick={modal.onClose}
            data-cy="modal-pay-by-link-detail-ok"
          >
            {t('common.ok')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm dialog */}
      <Dialog
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle data-cy="modal-pay-by-link-detail-delete-confirm-title">
          {t('modal.pay_by_link_detail.delete_confirm_title')}
        </DialogTitle>
        {submitR.kind === 'Loading' ? (
          <DialogContent>
            <LoadingBodyModal />
          </DialogContent>
        ) : (
          <DialogContent>
            <Typography
              variant="body2"
              whiteSpace="pre-line"
              data-cy="modal-pay-by-link-detail-delete-confirm"
            >
              {t('modal.pay_by_link_detail.delete_confirm', { itemId: modal.item.productId })}
            </Typography>
            {submitR.kind === 'Error' && (
              <Typography
                color="error"
                sx={{ mt: 2 }}
                data-cy="modal-pay-by-link-detail-delete-confirm-error"
              >
                {submitR.error.message}
              </Typography>
            )}
          </DialogContent>
        )}

        {/* Confirm dialog actions */}
        <DialogActions>
          <Button
            onClick={() => setShowDeleteConfirm(false)}
            disabled={submitR.kind === 'Loading'}
            data-cy="modal-pay-by-link-detail-delete-confirm-cancel"
          >
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleConfirmDelete}
            disabled={submitR.kind === 'Loading'}
            data-cy="modal-pay-by-link-detail-delete-confirm-submit"
          >
            {t('common.submit')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        open={snackbarOpen}
        autoHideDuration={2000}
        onClose={handleSnackbarClose}
        message={t('item.basic_info.copied_message')}
      />
    </>
  );
};

export default PayByLinkDetail;
